import { useState, useEffect } from 'react';
import { Download, RefreshCw, Eye, Code, FileText } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { logger } from '../../services/loggingService';
import { ManifestGeneratorService } from '../../services/manifestGeneratorService';
import toast from 'react-hot-toast';

interface ManifestViewerProps {
  projectId: string;
}

export function ManifestViewer({ projectId }: ManifestViewerProps) {
  const [manifest, setManifest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadManifest();
  }, [projectId]);

  const loadManifest = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('manifests')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') {
        logger.error('Error loading manifest', { error, projectId });
      }
      
      setManifest(data || null);
    } catch (error) {
      logger.error('Error loading manifest', { error, projectId });
      // Don't show error toast for missing manifest
    } finally {
      setLoading(false);
    }
  };

  const generateManifest = async () => {
    try {
      setGenerating(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser() || await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get blueprint ID from project and validate it's locked
      const { data: blueprint, error: blueprintError } = await supabase
        .from('project_blueprints')
        .select('id, is_locked')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (blueprintError) {
        logger.error('Error fetching blueprint', { error: blueprintError, projectId });
        throw new Error('Failed to fetch blueprint');
      }

      if (!blueprint) {
        throw new Error('No blueprint found. Please generate a blueprint first.');
      }

      // Validate blueprint is locked before generating manifest
      if (!blueprint.is_locked) {
        toast.error('Blueprint must be locked before generating manifest. Please lock the blueprint first.');
        return;
      }

      // Use ManifestGeneratorService instead of edge function
      const manifest = await ManifestGeneratorService.generateFromBlueprint(blueprint.id, user.id);

      setManifest(manifest);
      toast.success('Manifest generated successfully');
      await loadManifest(); // Reload to ensure UI is in sync
    } catch (error) {
      logger.error('Error generating manifest', { error, projectId });
      toast.error(error instanceof Error ? error.message : 'Failed to generate manifest');
    } finally {
      setGenerating(false);
    }
  };

  const downloadManifest = () => {
    if (!manifest) return;
    
    const blob = new Blob([JSON.stringify(manifest.content, null, 2)], { 
      type: 'application/json' 
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `manifest-${projectId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-text">Project Manifest</h3>
        <div className="flex gap-2">
          {manifest && (
            <button
              onClick={downloadManifest}
              className="btn-success inline-flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
          )}
          <button
            onClick={generateManifest}
            disabled={generating}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
            {manifest ? 'Regenerate' : 'Generate'}
          </button>
        </div>
      </div>

      {manifest ? (
        <div className="bg-background-secondary rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-accent-500" />
            <h4 className="font-semibold text-text">
              Manifest Generated
            </h4>
            <span className="text-sm text-text-secondary">
              {new Date(manifest.created_at).toLocaleString()}
            </span>
          </div>

          <div className="space-y-3">
            <div>
              <span className="font-medium text-text-secondary">Project Structure:</span>
              <div className="mt-1 text-sm text-text-secondary">
                {manifest.content?.fileStructure ? 
                  `${Object.keys(manifest.content.fileStructure).length} directories, ${manifest.content.fileStructure.totalFiles || 0} files` :
                  'Not available'
                }
              </div>
            </div>

            <div>
              <span className="font-medium text-text-secondary">Dependencies:</span>
              <div className="mt-1 text-sm text-text-secondary">
                {manifest.content?.dependencies ? 
                  `${Object.keys(manifest.content.dependencies).length} dependency groups` :
                  'Not available'
                }
              </div>
            </div>

            <div>
              <span className="font-medium text-text-secondary">Environment Variables:</span>
              <div className="mt-1 text-sm text-text-secondary">
                {manifest.content?.environmentVariables ? 
                  `${manifest.content.environmentVariables.length} variables defined` :
                  'Not available'
                }
              </div>
            </div>

            <div>
              <span className="font-medium text-text-secondary">API Endpoints:</span>
              <div className="mt-1 text-sm text-text-secondary">
                {manifest.content?.apiEndpoints ? 
                  `${manifest.content.apiEndpoints.length} endpoints defined` :
                  'Not available'
                }
              </div>
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={() => {
                // Create modal wrapper
                const modal = document.createElement('div');
                modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';

                // Create content container
                const contentDiv = document.createElement('div');
                contentDiv.className = 'bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[80vh] overflow-auto';

                // Create header
                const headerDiv = document.createElement('div');
                headerDiv.className = 'flex items-center justify-between mb-4';

                const heading = document.createElement('h3');
                heading.className = 'text-lg font-semibold text-gray-900 dark:text-white';
                heading.textContent = 'Manifest Content';

                const closeButton = document.createElement('button');
                closeButton.className = 'text-gray-400 hover:text-gray-600';
                closeButton.textContent = '×';
                closeButton.onclick = () => modal.remove();

                headerDiv.appendChild(heading);
                headerDiv.appendChild(closeButton);

                // Create pre element with safe content
                const pre = document.createElement('pre');
                pre.className = 'bg-gray-100 dark:bg-gray-700 p-4 rounded text-sm overflow-auto';
                pre.textContent = JSON.stringify(manifest.content, null, 2);

                // Assemble modal
                contentDiv.appendChild(headerDiv);
                contentDiv.appendChild(pre);
                modal.appendChild(contentDiv);
                document.body.appendChild(modal);

                // Close on background click
                modal.addEventListener('click', (e) => {
                  if (e.target === modal) modal.remove();
                });
              }}
              className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              <Eye className="w-4 h-4" />
              View Full Manifest
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <Code className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">No manifest generated yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
            Generate a manifest to define your project structure and configuration
          </p>
        </div>
      )}
    </div>
  );
}